// ============================================================
//  useTabManager Hook — Dynamic Tab State Management
// ============================================================

import { useReducer, useCallback } from 'react';
import {
  DynamicTab,
  TabManagerState,
  TabAction,
  DEFAULT_TAB_SETTINGS,
  DEFAULT_TABS,
} from '../types/tabs';

// Tab Manager Reducer
function tabManagerReducer(
  state: TabManagerState,
  action: TabAction
): TabManagerState {
  switch (action.type) {
    case 'SET_TABS':
      return {
        ...state,
        tabs: action.payload.sort(
          (a: DynamicTab, b: DynamicTab) => a.order - b.order
        ),
        isInitialized: true,
      };

    case 'ADD_TAB':
      // Generate slug from label
      const baseSlug = (action.payload.label || 'nouvelle-page')
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, ''); // Remove special characters

      // Ensure uniqueness by checking existing tabs
      let uniqueSlug = baseSlug;
      let counter = 1;
      while (state.tabs.some((t) => t.id === uniqueSlug)) {
        uniqueSlug = `${baseSlug}-${counter}`;
        counter++;
      }

      const newTab: DynamicTab = {
        id: uniqueSlug, // Use the same value for both id and slug
        label: action.payload.label || 'Nouvelle page',
        slug: uniqueSlug,
        order: Math.max(...state.tabs.map((t) => t.order), -1) + 1,
        isDefault: false,
        isActive: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const newState = {
        ...state,
        tabs: [...state.tabs, newTab].sort((a, b) => a.order - b.order),
        activeTab: newTab.id,
      };
      return newState;

    case 'UPDATE_TAB':
      return {
        ...state,
        tabs: state.tabs.map((tab) =>
          tab.id === action.payload.id
            ? {
                ...tab,
                ...action.payload.updates,
                updatedAt: new Date().toISOString(),
              }
            : tab
        ),
      };

    case 'DELETE_TAB':
      const remainingTabs = state.tabs.filter(
        (tab) => tab.id !== action.payload.id
      );
      const newActiveTab =
        state.activeTab === action.payload.id
          ? remainingTabs[0]?.id || state.tabs[0]?.id
          : state.activeTab;

      return {
        ...state,
        tabs: remainingTabs,
        activeTab: newActiveTab,
        editing: null,
      };

    case 'REORDER_TABS':
      const { fromIndex, toIndex } = action.payload;
      const reorderedTabs = [...state.tabs];
      const [movedTab] = reorderedTabs.splice(fromIndex, 1);
      reorderedTabs.splice(toIndex, 0, movedTab);

      // Update order property
      const updatedTabs = reorderedTabs.map((tab, index) => ({
        ...tab,
        order: index,
        updatedAt: new Date().toISOString(),
      }));

      return {
        ...state,
        tabs: updatedTabs,
        draggedTab: null,
      };

    case 'SET_ACTIVE':
      return {
        ...state,
        activeTab: action.payload.id,
        editing: null,
      };

    case 'SET_EDITING':
      return {
        ...state,
        editing: action.payload.id,
      };

    case 'SET_DRAGGING':
      return {
        ...state,
        draggedTab: action.payload.id,
      };

    default:
      return state;
  }
}

// Initial state - simplified without localStorage dependency
const getInitialState = (): TabManagerState => ({
  tabs: DEFAULT_TABS,
  activeTab: DEFAULT_TABS[0]?.id || 'accueil',
  editing: null,
  draggedTab: null,
  settings: DEFAULT_TAB_SETTINGS,
  isInitialized: true,
});

export function useTabManager(initialTabs?: DynamicTab[]): any {
  // Initialize with default tabs or provided tabs
  const [state, dispatch] = useReducer(tabManagerReducer, {
    tabs: initialTabs?.length ? initialTabs : DEFAULT_TABS,
    activeTab: initialTabs?.length
      ? initialTabs[0].id
      : DEFAULT_TABS[0]?.id || 'accueil',
    editing: null,
    draggedTab: null,
    settings: DEFAULT_TAB_SETTINGS,
    isInitialized: true,
  });

  // Actions
  const addTab = useCallback(
    (label: string) => {
      if (state.tabs.length >= state.settings.maxTabs) {
        console.warn('Cannot add more tabs: maximum reached');
        return;
      }
      dispatch({
        type: 'ADD_TAB',
        payload: { label },
      });
    },
    [state.tabs.length, state.settings.maxTabs]
  );

  const updateTab = useCallback((id: string, updates: Partial<DynamicTab>) => {
    dispatch({
      type: 'UPDATE_TAB',
      payload: { id, updates },
    });
  }, []);

  const deleteTab = useCallback(
    (id: string) => {
      const tab = state.tabs.find((t) => t.id === id);
      if (tab?.isDefault) {
        console.warn('Cannot delete default tab');
        return;
      }
      if (state.tabs.length <= 1) {
        console.warn('Cannot delete the last tab');
        return;
      }
      dispatch({
        type: 'DELETE_TAB',
        payload: { id },
      });
    },
    [state.tabs]
  );

  const setTabs = useCallback((tabs: DynamicTab[]) => {
    dispatch({
      type: 'SET_TABS',
      payload: tabs.length ? tabs : DEFAULT_TABS,
    });
  }, []);

  const reorderTabs = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!state.settings.allowReorder) return;
      dispatch({
        type: 'REORDER_TABS',
        payload: { fromIndex, toIndex },
      });
    },
    [state.settings.allowReorder]
  );

  const setActiveTab = useCallback((id: string) => {
    dispatch({
      type: 'SET_ACTIVE',
      payload: { id },
    });
  }, []);

  const setEditingTab = useCallback((id: string | null) => {
    dispatch({
      type: 'SET_EDITING',
      payload: { id },
    });
  }, []);

  const setDraggedTab = useCallback((id: string | null) => {
    dispatch({
      type: 'SET_DRAGGING',
      payload: { id },
    });
  }, []);

  // Helpers
  const getActiveTab = useCallback(() => {
    return state.tabs.find((tab) => tab.id === state.activeTab);
  }, [state.tabs, state.activeTab]);

  const getTabBySlug = useCallback(
    (slug: string) => {
      return state.tabs.find((tab) => tab.slug === slug);
    },
    [state.tabs]
  );

  const canDeleteTab = useCallback(
    (id: string) => {
      const tab = state.tabs.find((t) => t.id === id);
      return (
        state.settings.allowDelete && !tab?.isDefault && state.tabs.length > 1
      );
    },
    [state.tabs, state.settings.allowDelete]
  );

  const canAddTab = useCallback(() => {
    return (
      state.settings.allowAdd && state.tabs.length < state.settings.maxTabs
    );
  }, [state.tabs.length, state.settings]);

  const getTabs = useCallback(() => state.tabs, [state.tabs]);

  return {
    // State
    tabs: state.tabs,
    activeTab: state.activeTab,
    editing: state.editing,
    draggedTab: state.draggedTab,
    settings: state.settings,
    isInitialized: state.isInitialized,

    // Actions
    addTab,
    updateTab,
    deleteTab,
    setTabs,
    reorderTabs,
    setActiveTab,
    setEditingTab,
    setDraggedTab,

    // Helpers
    getActiveTab,
    getTabBySlug,
    canDeleteTab,
    canAddTab,

    getTabs,
  };
}
